// src/context/NewSoftwareConfigContext.tsx
import { createContext as createContext4, useContext as useContext4, useMemo } from "react";
import { ThemeProvider as StyledThemeProvider } from "styled-components";
import { findMessage } from "@newsoftware-platform/newsoftware-lib-node";

// src/styles/GlobalStyle.ts
import { createGlobalStyle } from "styled-components";
var GlobalStyle = createGlobalStyle`

  *, ::before, ::after {
    box-sizing: border-box;
  }

  html, body {
    margin: 0;
    padding: 0;
    font-family: "Poppins", sans-serif;
    font-size: 0.938rem;
  }

  body {
    background-color: ${({ theme }) => theme?.background || "#fff"};
    color: ${({ theme }) => theme?.text || "#000"};
    transition: background-color 0.3s ease, color 0.3s ease;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  html {
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }

  h1, h2, h3, a {
    margin: 0;
  }

  a {
    text-decoration: none;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  .toast {
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    min-width: 280px;
    max-width: 80%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 20px;
    border-radius: 6px;
    color: white;
    font-weight: 500;
    font-size: 16px;
    animation: fadeInOut 4s ease-in-out;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    z-index: 1000;
  }

  .toast-success {
    background-color: #4caf50;
  }

  .toast-error {
    background-color: #f44336;
  }

  .toast-info {
    background-color: #2196f3;
  }

  .toast i {
    font-size: 18px;
    opacity: 0.85;
  }

  @keyframes fadeInOut {
    0% {
      opacity: 0;
      transform: translateY(30px);
    }
    10% {
      opacity: 1;
      transform: translateY(0);
    }
    90% {
      opacity: 1;
      transform: translateY(0);
    }
    100% {
      opacity: 0;
      transform: translateY(30px);
    }
  }

  .fade-in {
    opacity: 0;
    animation: fadeIn 0.3s ease forwards;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// src/styles/themes.ts
var lightTheme = {
  background: "#f9f6fd",
  text: "#333333",
  headerBackground: "#f5f5f5",
  shadowColor: "#111",
  //E5E8EB //111
  error: "#721c24",
  border: "#ddd",
  button: "#007bff"
};
var darkTheme = {
  background: "#1e1e1e",
  text: "#ffffff",
  headerBackground: "#222",
  shadowColor: "#000",
  error: "#721c24",
  border: "#ddd",
  button: "#007bff"
};

// src/context/NewSoftwareThemeContext.tsx
import { createContext, useContext, useState } from "react";
import { jsx } from "react/jsx-runtime";
var supportedThemes = ["light", "dark"];
var ThemeContext = createContext(void 0);
function useThemeMode() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useThemeMode must be used within ThemeProvider");
  return context;
}
var ThemeProvider = ({ children, initialTheme = "light" }) => {
  const [themeMode, setThemeMode] = useState(initialTheme);
  const toggleTheme = (mode) => {
    if (mode === "light" || mode === "dark") {
      setThemeMode(mode);
    } else {
      setThemeMode((prev) => prev === "light" ? "dark" : "light");
    }
  };
  return /* @__PURE__ */ jsx(ThemeContext.Provider, { value: { themeMode, toggleTheme }, children });
};

// src/context/NewSoftwareLangContext.tsx
import { createContext as createContext2, useContext as useContext2, useState as useState2 } from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var supportedLangs = ["en", "pt"];
var LangContext = createContext2(void 0);
function useLang() {
  const context = useContext2(LangContext);
  if (!context) throw new Error("useLang must be used within a LangProvider");
  return context;
}
var LangProvider = ({
  children,
  initialLang = "pt"
}) => {
  const [lang, setLang] = useState2(initialLang);
  const setLangMode = (mode) => {
    if (mode) setLang(mode);
    else setLang((prev) => prev === "en" ? "pt" : "en");
  };
  return /* @__PURE__ */ jsx2(LangContext.Provider, { value: { langMode: lang, setLangMode }, children });
};

// src/context/NewSoftwareAlertContext.tsx
import { createContext as createContext3, useState as useState3, useContext as useContext3 } from "react";
import { jsx as jsx3 } from "react/jsx-runtime";
var AlertContext = createContext3({
  text: "",
  type: "",
  setAlert: () => {
  }
});
var useAlert = () => useContext3(AlertContext);
var AlertProvider = ({ children }) => {
  const [text, setText] = useState3("");
  const [type, setType] = useState3("");
  const setAlert = (text2, type2) => {
    setText(text2);
    setType(type2);
    setTimeout(() => {
      setText("");
      setType("");
    }, 4e3);
  };
  return /* @__PURE__ */ jsx3(AlertContext.Provider, { value: { text, type, setAlert }, children });
};

// src/styles/NewSoftwareStyle.ts
var contentMain = () => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  width: "100%",
  padding: "0 16px",
  boxSizing: "border-box",
  overflow: "hidden"
});
var contentContainer = (theme) => ({
  flex: 1,
  width: "100%",
  padding: "10px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  backgroundColor: theme.background,
  overflowY: "auto"
  // O scroll acontece apenas aqui dentro
});
var contentTitle = () => ({
  fontWeight: "bold",
  fontSize: "8px",
  padding: "8px 12px"
});
var pageMain = () => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100vh",
  fontFamily: "'Montserrat', sans-serif"
});
var pageContainer = (theme) => ({
  background: theme.background,
  padding: "40px 60px",
  borderRadius: "10px",
  boxShadow: "0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22)",
  maxWidth: "360px",
  width: "100%",
  textAlign: "center"
});
var pageErrorTitle = (theme) => ({
  fontWeight: 800,
  fontSize: "24px",
  color: theme.error || "#721c24",
  // Fallback para vermelho de erro
  margin: "20px 0"
});
var headerMain = (theme) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  alignContent: "center",
  backgroundColor: theme.background,
  boxShadow: `0px 0px 5px 0px ${theme.shadowColor}`,
  padding: "0px",
  height: "80px",
  zIndex: 1300
});
var headerContainer = () => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  alignContent: "center",
  padding: "0px"
});
var headerContainerLogo = () => ({
  paddingLeft: "2px",
  height: "100%"
});
var headerLogo = () => ({
  padding: "5px",
  height: "80px"
});
var headerContainerLabel = (bold = false) => ({
  fontWeight: bold ? "bold" : "normal",
  margin: "0 20px",
  color: "#666"
});
var headerContainerDropdow = () => ({
  position: "absolute",
  right: "30px",
  cursor: "pointer",
  zIndex: 1,
  display: "flex",
  boxSizing: "border-box"
});
var footerMain = (theme) => ({
  padding: "10px",
  backgroundColor: theme.background,
  textAlign: "center"
});
var footerText = (bold = false) => ({
  fontWeight: bold ? "bold" : "normal",
  margin: "0 4px",
  color: "#666"
});
var dropdownButton = () => ({
  paddingLeft: "5px",
  paddingRight: "5px",
  cursor: "pointer"
});
var dropdownFrame = () => ({
  position: "absolute",
  right: "0",
  listStyleType: "none",
  margin: "5px 0",
  padding: "0",
  background: "white",
  minWidth: "150px",
  minHeight: "150px",
  cursor: "none",
  borderRadius: "8px",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)"
});
var headerLangButton = (current, selected) => ({
  padding: "5px 5px",
  margin: "0 2px",
  border: "1px solid #ccc",
  borderRadius: "2px",
  backgroundColor: selected === current ? "#007bff" : "#f0f0f0",
  color: selected === current ? "white" : "black",
  cursor: "pointer"
});
var headerThemeButton = (current, selected) => ({
  padding: "5px 5px",
  margin: "0 2px",
  border: "1px solid #ccc",
  borderRadius: "2px",
  backgroundColor: selected === current ? "#007bff" : "#f0f0f0",
  color: selected === current ? "white" : "black",
  cursor: "pointer"
});
var toastStyles = {
  base: {
    position: "fixed",
    bottom: "20px",
    left: "50%",
    transform: "translateX(-50%)",
    minWidth: "280px",
    maxWidth: "80%",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "14px 20px",
    borderRadius: "6px",
    color: "white",
    fontWeight: 500,
    fontSize: "16px",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
    zIndex: 1e3,
    animation: "fadeInOut 4s ease-in-out"
  },
  icon: {
    fontSize: "18px",
    opacity: 0.85
  }
};
var toastVariants = {
  success: { backgroundColor: "#4caf50" },
  error: { backgroundColor: "#f44336" },
  info: { backgroundColor: "#2196f3" }
};
var formFrame = () => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center"
});
var formTitle = (theme) => ({
  marginTop: "50px",
  fontWeight: 800,
  marginBottom: "20px",
  color: theme.text || "#333",
  textAlign: "center"
});
var formLabel = (theme) => ({
  display: "block",
  textAlign: "left",
  margin: "16px 0 6px",
  fontWeight: 600,
  fontSize: "14px",
  color: theme.text || "#212b36"
});
var formInput = (theme, isFocused) => ({
  width: "100%",
  padding: "10px 14px",
  margin: 0,
  border: `1px solid ${isFocused ? "#007bff" : "#dcdcdc"}`,
  borderRadius: "8px",
  fontSize: "15px",
  transition: "all 0.3s ease",
  backgroundColor: "#fff",
  boxSizing: "border-box",
  outline: "none",
  boxShadow: isFocused ? "0 0 0 2px rgba(0, 123, 255, 0.2)" : "none"
});
var formInputFocus = (theme) => ({
  borderColor: theme.button || "#007bff",
  outline: "none",
  boxShadow: `0 0 0 3px ${theme.button}33`,
  transition: "all 0.2s ease-in-out"
});
var formButton = (theme) => ({
  width: "100%",
  padding: "12px",
  marginTop: "24px",
  border: "none",
  borderRadius: "4px",
  backgroundColor: theme.button || "#007bff",
  color: "white",
  fontWeight: 800,
  letterSpacing: "1px",
  transition: "background-color 0.3s ease",
  cursor: "pointer"
});

// src/components/Toast.tsx
import { jsx as jsx4, jsxs } from "react/jsx-runtime";
var getIconClass = (type) => {
  switch (type) {
    case "success":
      return "fa fa-check-circle";
    case "error":
      return "fa fa-times-circle";
    case "info":
      return "fa fa-info-circle";
    default:
      return "";
  }
};
var Toast = () => {
  const { text, type } = useAlert();
  if (!text) return null;
  const combinedStyle = {
    ...toastStyles.base,
    ...toastVariants[type] || {}
  };
  return /* @__PURE__ */ jsxs("div", { style: combinedStyle, children: [
    /* @__PURE__ */ jsx4("i", { className: getIconClass(type), style: toastStyles.icon }),
    /* @__PURE__ */ jsx4("span", { children: text })
  ] });
};
var Toast_default = Toast;

// src/context/NewSoftwareConfigContext.tsx
import { jsx as jsx5, jsxs as jsxs2 } from "react/jsx-runtime";
var ConfigContext = createContext4(null);
var useConfig = () => {
  const context = useContext4(ConfigContext);
  if (!context) throw new Error("useConfig must be used within ConfigProvider");
  return context;
};
var useAppName = () => useConfig().name;
var useViewNode = () => useConfig().viewNode;
var useTranslation = () => {
  const config = useConfig();
  const langContext = useLang();
  const lang = langContext?.langMode || "en";
  const viewData = config?.viewNode;
  const rootNode = viewData?.root;
  const t = (key) => {
    if (!rootNode) return key;
    return findMessage(rootNode, config.name, lang, key) ?? key;
  };
  return { t };
};
var StyledThemeBridge = ({ children }) => {
  const { themeMode } = useThemeMode();
  const currentTheme = themeMode === "light" ? lightTheme : darkTheme;
  return /* @__PURE__ */ jsxs2(StyledThemeProvider, { theme: currentTheme, children: [
    /* @__PURE__ */ jsx5(GlobalStyle, {}),
    children
  ] });
};
var ConfigProvider = ({
  children,
  name,
  viewNode,
  lang = "en",
  theme = "light"
}) => {
  const configValue = useMemo(() => ({
    name: name || "newsoftware",
    viewNode
  }), [name, viewNode]);
  return /* @__PURE__ */ jsx5(ThemeProvider, { initialTheme: theme, children: /* @__PURE__ */ jsx5(LangProvider, { initialLang: lang, children: /* @__PURE__ */ jsx5(StyledThemeBridge, { children: /* @__PURE__ */ jsx5(ConfigContext.Provider, { value: configValue, children: /* @__PURE__ */ jsxs2(AlertProvider, { children: [
    children,
    /* @__PURE__ */ jsx5(Toast_default, {})
  ] }) }) }) }) });
};

// src/context/NewSoftwareAuthContext.tsx
import React5, { createContext as createContext5, useState as useState4, useEffect } from "react";
import { jwtDecode } from "jwt-decode";

// src/service/axiosInstance.ts
import axios from "axios";
var base_url = process.env.REACT_APP_API_URL || "";
var instance = axios.create({ baseURL: base_url });
instance.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
instance.interceptors.response.use((res) => res, async (err) => {
  const original = err.config;
  if (err.response?.status === 401 && !original._retry) {
    original._retry = true;
    const res = await axios.post("/auth/refresh", {
      //const res = await axios.post('http://localhost:4000/auth/refresh', {
      refreshToken: localStorage.getItem("refreshToken")
    });
    const newToken = res.data.accessToken;
    localStorage.setItem("accessToken", newToken);
    original.headers.Authorization = `Bearer ${newToken}`;
    return instance(original);
  }
  return Promise.reject(err);
});
var axiosInstance_default = instance;

// src/context/NewSoftwareAuthContext.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var AuthContext = createContext5(null);
var AuthProvider = ({ children }) => {
  const [token, setToken] = useState4(localStorage.getItem("accessToken"));
  const [user, setUser] = useState4(token ? jwtDecode(token) : null);
  const login = async (workspace, username, password) => {
    const res = await axiosInstance_default.post("/auth/login", { workspace, username, password });
    const accessToken = res.data.token;
    setToken(accessToken);
    setUser(jwtDecode(accessToken));
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", res.data.refreshToken);
    return true;
  };
  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.clear();
  };
  useEffect(() => {
    if (token) {
      const decoded = jwtDecode(token);
      const timeout = decoded.exp * 1e3 - Date.now();
      const timer = setTimeout(logout, timeout);
      return () => clearTimeout(timer);
    }
  }, [token]);
  return /* @__PURE__ */ jsx6(AuthContext.Provider, { value: { token, user, login, logout }, children });
};
var useAuth = () => React5.useContext(AuthContext);

// src/context/NewSoftwareShell.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
var NewSoftwareShell = ({ children, name, viewData, lang, theme }) => {
  return /* @__PURE__ */ jsx7(ConfigProvider, { name, viewNode: viewData, lang, theme, children: /* @__PURE__ */ jsx7(AuthProvider, { children }) });
};

// src/context/NewSoftwarePrivateRoute.tsx
import { Navigate } from "react-router-dom";
import { Fragment, jsx as jsx8 } from "react/jsx-runtime";
var PrivateRoute = ({ children, requered }) => {
  const { user } = useAuth();
  if (requered === "TRUE") {
    return user ? /* @__PURE__ */ jsx8(Fragment, { children }) : /* @__PURE__ */ jsx8(Navigate, { to: "/login" });
  }
  return /* @__PURE__ */ jsx8(Fragment, { children });
};

// src/page/ContentPage.tsx
import { useParams } from "react-router-dom";
import { findMenu as findMenu2 } from "@newsoftware-platform/newsoftware-lib-node";

// src/components/Header.tsx
import { useEffect as useEffect3, useRef as useRef2, useState as useState6 } from "react";
import { Link as Link2 } from "react-router-dom";

// src/components/Dropdown.tsx
import { jsx as jsx9, jsxs as jsxs3 } from "react/jsx-runtime";
var Dropdown = ({ name, isOpen, onToggle, children }) => {
  return /* @__PURE__ */ jsxs3("div", { onClick: () => onToggle(name), children: [
    /* @__PURE__ */ jsx9("span", { className: `fa fa-${name}`, style: dropdownButton() }),
    isOpen && /* @__PURE__ */ jsx9("div", { style: dropdownFrame(), className: "fade-in", children })
  ] });
};

// src/components/Navbar.tsx
import { useState as useState5, useRef, useEffect as useEffect2 } from "react";
import { Link, useLocation } from "react-router-dom";
import { findValue } from "@newsoftware-platform/newsoftware-lib-node";
import { jsx as jsx10, jsxs as jsxs4 } from "react/jsx-runtime";
var Navbar = ({ items }) => {
  const location = useLocation();
  const containerRef = useRef(null);
  const [activeDropdown, setActiveDropdown] = useState5(null);
  const { themeMode } = useThemeMode();
  const activeTheme = themeMode === "light" ? lightTheme : darkTheme;
  useEffect2(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const hasContent = (node) => {
    if (!node.nodes || node.nodes.length === 0) return false;
    return node.nodes.some((child) => {
      if (child.name === "menu_collection") return hasContent(child);
      return true;
    });
  };
  const renderItems = (nodes, level = 0) => {
    return nodes.map((item) => {
      if (item.name === "menu_collection") {
        return item.nodes ? renderItems(item.nodes, level) : null;
      }
      const item_label = findValue(item, "label") ?? item.name;
      const item_path = findValue(item, "event");
      const item_icon = findValue(item, "icon");
      const hasRealChildren = hasContent(item);
      const isTopLevel = level === 0;
      const isOpen = activeDropdown === item.name;
      return /* @__PURE__ */ jsxs4(
        "div",
        {
          style: { position: "relative" },
          onMouseEnter: () => hasRealChildren && setActiveDropdown(item.name),
          onMouseLeave: () => isTopLevel && setActiveDropdown(null),
          children: [
            /* @__PURE__ */ jsxs4(
              "div",
              {
                style: {
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  padding: "10px 15px",
                  color: activeTheme.text,
                  backgroundColor: isOpen ? "rgba(0,0,0,0.05)" : "transparent",
                  whiteSpace: "nowrap"
                },
                children: [
                  item_icon && /* @__PURE__ */ jsx10("i", { className: item_icon, style: { marginRight: 8 } }),
                  /* @__PURE__ */ jsx10("div", { style: { flex: 1 }, children: item_path ? /* @__PURE__ */ jsx10(
                    Link,
                    {
                      to: item_path,
                      style: {
                        textDecoration: "none",
                        color: activeTheme.text,
                        fontWeight: location.pathname === item_path ? "bold" : "normal"
                      },
                      children: item_label
                    }
                  ) : /* @__PURE__ */ jsx10("span", { children: item_label }) }),
                  hasRealChildren && /* @__PURE__ */ jsx10("span", { style: { marginLeft: 8, fontSize: "0.8em" }, children: isTopLevel ? "\u25BE" : "\u25B8" })
                ]
              }
            ),
            hasRealChildren && isOpen && /* @__PURE__ */ jsx10(
              "div",
              {
                onMouseLeave: () => setActiveDropdown(null),
                style: {
                  position: "absolute",
                  top: isTopLevel ? "100%" : 0,
                  left: isTopLevel ? 0 : "100%",
                  backgroundColor: activeTheme.background,
                  boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
                  border: `1px solid ${activeTheme.border}`,
                  zIndex: 999,
                  minWidth: "180px"
                },
                children: renderItems(item.nodes, level + 1)
              }
            )
          ]
        },
        item.name
      );
    });
  };
  return /* @__PURE__ */ jsx10(
    "header",
    {
      ref: containerRef,
      style: {
        display: "flex",
        flexDirection: "row",
        // Garante horizontalidade
        backgroundColor: activeTheme.background,
        borderBottom: "none",
        // REMOVE A LINHA NO BOTTOM
        height: "50px",
        alignItems: "center",
        padding: "0 20px"
      },
      children: items?.nodes ? renderItems(
        items.name === "menu_collection" ? items.nodes || [] : items.nodes
      ) : null
    }
  );
};

// src/components/Header.tsx
import { findMenu } from "@newsoftware-platform/newsoftware-lib-node";
import { Fragment as Fragment2, jsx as jsx11, jsxs as jsxs5 } from "react/jsx-runtime";
var Header = ({ load, name }) => {
  const { t } = useTranslation();
  const appName = useAppName();
  const viewData = useViewNode();
  const rootNode = viewData?.root;
  const menuNode = findMenu(rootNode, appName, "MAIN");
  const { langMode, setLangMode } = useLang();
  const { themeMode, toggleTheme } = useThemeMode();
  const activeTheme = themeMode === "light" ? lightTheme : darkTheme;
  const { logout, user } = useAuth();
  const [openDropdown, setOpenDropdown] = useState6(null);
  const containerRef = useRef2(null);
  const handleToggle = (dropdownName) => {
    setOpenDropdown((prev) => prev === dropdownName ? null : dropdownName);
  };
  useEffect3(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpenDropdown(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
  return /* @__PURE__ */ jsx11("header", { style: headerMain(activeTheme), children: /* @__PURE__ */ jsxs5("div", { style: headerContainer(), children: [
    /* @__PURE__ */ jsx11(Link2, { style: headerContainerLogo(), to: "/", children: /* @__PURE__ */ jsx11("img", { src: `/images/${appName}-logo.png`, alt: appName, style: headerLogo() }) }),
    /* @__PURE__ */ jsx11("span", { style: headerContainerLabel(true), children: load.includes("slogan") && `${t("txtSlogan1")} ${t("txtSlogan2")}` }),
    /* @__PURE__ */ jsx11("span", { children: load.includes("navbar") && menuNode && /* @__PURE__ */ jsx11(Navbar, { items: menuNode }) }),
    /* @__PURE__ */ jsxs5("div", { ref: containerRef, style: headerContainerDropdow(), children: [
      load.includes("identity") && /* @__PURE__ */ jsxs5(Fragment2, { children: [
        /* @__PURE__ */ jsx11(Dropdown, { name: "bell", isOpen: openDropdown === "bell", onToggle: handleToggle, children: /* @__PURE__ */ jsxs5("ul", { children: [
          /* @__PURE__ */ jsxs5("li", { children: [
            t("txtNotification"),
            " 1"
          ] }),
          /* @__PURE__ */ jsxs5("li", { children: [
            t("txtNotification"),
            " 2"
          ] })
        ] }) }),
        /* @__PURE__ */ jsx11(Dropdown, { name: "user", isOpen: openDropdown === "user", onToggle: handleToggle, children: /* @__PURE__ */ jsxs5("div", { style: { padding: "10px" }, children: [
          /* @__PURE__ */ jsxs5("strong", { children: [
            t("userLabelName"),
            ": ",
            user?.sub
          ] }),
          /* @__PURE__ */ jsx11("br", {}),
          /* @__PURE__ */ jsx11(
            "button",
            {
              style: { marginTop: "10px", cursor: "pointer" },
              onClick: () => logout(),
              children: t("userButtonLogout")
            }
          )
        ] }) })
      ] }),
      /* @__PURE__ */ jsx11(Dropdown, { name: "gear", isOpen: openDropdown === "gear", onToggle: handleToggle, children: /* @__PURE__ */ jsxs5("div", { style: { padding: "10px", display: "flex", flexDirection: "column", gap: "8px" }, children: [
        /* @__PURE__ */ jsx11("div", { style: { display: "flex", gap: "5px" }, children: supportedLangs.map((lang) => /* @__PURE__ */ jsx11(
          "button",
          {
            style: headerLangButton(lang, langMode),
            onClick: () => setLangMode(lang),
            children: lang.toUpperCase()
          },
          lang
        )) }),
        /* @__PURE__ */ jsx11("div", { style: { display: "flex", gap: "5px" }, children: supportedThemes.map((theme) => /* @__PURE__ */ jsx11(
          "button",
          {
            style: headerThemeButton(theme, themeMode),
            onClick: () => toggleTheme(theme),
            children: theme.toUpperCase()
          },
          theme
        )) })
      ] }) })
    ] })
  ] }) });
};

// src/components/Footer.tsx
import { Link as Link3 } from "react-router-dom";
import { jsx as jsx12, jsxs as jsxs6 } from "react/jsx-runtime";
var Footer = ({ application }) => {
  const { t } = useTranslation();
  const { themeMode } = useThemeMode();
  const activeTheme = themeMode === "light" ? lightTheme : darkTheme;
  return /* @__PURE__ */ jsxs6("footer", { style: footerMain(activeTheme), children: [
    /* @__PURE__ */ jsx12("span", { style: footerText(false), children: t("txtCopyright") }),
    /* @__PURE__ */ jsx12(Link3, { to: "/content/html/PrivacyPolicy", children: /* @__PURE__ */ jsx12("span", { style: footerText(false), children: t("txtPrivacyPolicy") }) }),
    /* @__PURE__ */ jsx12("span", { style: footerText(false), children: ",\xA0" }),
    /* @__PURE__ */ jsx12(Link3, { to: "/content/html/TermsConditions", children: /* @__PURE__ */ jsx12("span", { style: footerText(false), children: t("txTermsConditions") }) })
  ] });
};

// src/components/Sidebar.tsx
import { useState as useState7 } from "react";
import { Link as Link4, useLocation as useLocation2 } from "react-router-dom";
import { findValue as findValue2 } from "@newsoftware-platform/newsoftware-lib-node";

// src/styles/layoutStyle.ts
var pageStyle = () => ({
  height: "100vh",
  display: "flex",
  flexDirection: "column"
});
var pageContainerStyle = () => ({
  flex: 1,
  display: "flex",
  flexDirection: "row",
  overflow: "hidden"
});
var sidebarStyle = (theme) => ({
  position: "sticky",
  top: 0,
  left: 0,
  height: "90vh",
  width: "250px",
  //backgroundColor: '#F9FAFB',
  //borderRight: '1px solid #E5E8EB',
  backgroundColor: theme.background,
  //borderRight: `5px solid ${theme.shadowColor}`,
  //boxShadow: `0px 0px 5px 0px ${theme.shadowColor}`,
  //boxShadow: `1px 0px 1px 0px ${theme.shadowColor}`,
  boxShadow: `1px 0px 1px 0px #E5E8EB`,
  padding: "24px 16px",
  boxSizing: "border-box",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  zIndex: 1200
});
var sidebarItemStyle = (isActive = false) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "2px 16px",
  borderRadius: "8px",
  color: isActive ? "#2065D1" : "#637381",
  backgroundColor: isActive ? "#E3F2FD" : "transparent",
  fontWeight: 500,
  fontSize: "14px",
  textDecoration: "none",
  cursor: "pointer",
  transition: "all 0.3s ease",
  userSelect: "none"
});

// src/components/Sidebar.tsx
import { jsx as jsx13, jsxs as jsxs7 } from "react/jsx-runtime";
var Sidebar = ({ items }) => {
  const location = useLocation2();
  const [openItems, setOpenItems] = useState7({});
  const { t } = useTranslation();
  const { themeMode } = useThemeMode();
  const activeTheme = themeMode === "light" ? lightTheme : darkTheme;
  const toggleItem = (label) => {
    setOpenItems((prev) => ({
      ...prev,
      [label]: !prev[label]
    }));
  };
  const hasContent = (node) => {
    if (!node.nodes || node.nodes.length === 0) return false;
    return node.nodes.some((child) => {
      if (child.name === "menu_collection") {
        return hasContent(child);
      }
      return true;
    });
  };
  const renderItems = (nodes, level = 0) => {
    return nodes.map((item) => {
      if (item.name === "menu_collection") {
        return item.nodes ? renderItems(item.nodes, level) : null;
      }
      const item_label = findValue2(item, "label") ?? item.name;
      const item_path = findValue2(item, "event");
      const item_icon = findValue2(item, "icon");
      const hasRealChildren = hasContent(item);
      const isOpen = openItems[item.name] ?? false;
      return /* @__PURE__ */ jsxs7("div", { children: [
        /* @__PURE__ */ jsxs7(
          "div",
          {
            style: {
              display: "flex",
              alignItems: "center",
              cursor: hasRealChildren ? "pointer" : "default",
              padding: "6px 12px",
              marginLeft: level * 12,
              fontWeight: hasRealChildren ? "bold" : "normal",
              color: activeTheme.text
            },
            onClick: () => hasRealChildren && toggleItem(item.name),
            children: [
              item_icon && /* @__PURE__ */ jsx13("i", { className: item_icon, style: { marginRight: 8, width: 18 } }),
              /* @__PURE__ */ jsx13("div", { style: { flex: 1 }, children: item_path ? /* @__PURE__ */ jsx13(Link4, { to: item_path, style: sidebarItemStyle(item_path === location.pathname), children: item_label }) : /* @__PURE__ */ jsx13("span", { children: item_label }) }),
              hasRealChildren && /* @__PURE__ */ jsx13("span", { style: { marginLeft: "auto", fontSize: "1.1em" }, children: isOpen ? "\u25BE" : "\u25B8" })
            ]
          }
        ),
        hasRealChildren && isOpen && renderItems(item.nodes, level + 1)
      ] }, item.name);
    });
  };
  return /* @__PURE__ */ jsx13("aside", { style: sidebarStyle(activeTheme), children: items?.nodes ? renderItems(
    items.name === "menu_collection" ? items.nodes || [] : items.nodes
  ) : null });
};

// src/components/HtmlContent.tsx
import { useEffect as useEffect4, useState as useState8 } from "react";
import { jsx as jsx14 } from "react/jsx-runtime";
var HtmlContent = ({ name }) => {
  const [html, setHtml] = useState8("");
  const [loading, setLoading] = useState8(true);
  useEffect4(() => {
    if (!name) return;
    const filePath = `/html/${name}.html`;
    setLoading(true);
    fetch(filePath).then((response) => {
      if (!response.ok) throw new Error("Falha ao carregar HTML");
      return response.text();
    }).then((data) => setHtml(data)).catch((err) => {
      console.error(err);
      setHtml("<p>Erro ao carregar o conte\xFAdo solicitado.</p>");
    }).finally(() => setLoading(false));
  }, [name]);
  if (loading) return /* @__PURE__ */ jsx14("div", { children: "Carregando..." });
  return /* @__PURE__ */ jsx14("div", { dangerouslySetInnerHTML: { __html: html } });
};

// src/components/ViewContent.tsx
import { findForm } from "@newsoftware-platform/newsoftware-lib-node";

// src/components/view/NodeTreeView.tsx
import { useState as useState10, useEffect as useEffect5 } from "react";

// src/styles/viewStyle.ts
var containerStyle = (theme) => ({
  borderRadius: "2px",
  padding: "2px",
  //border: `1px solid ${theme.shadowColor}`,
  border: `1px dotted #ff0000`,
  backgroundColor: theme.background,
  display: "flex",
  flexDirection: "column",
  height: "80vh",
  overflowY: "auto",
  overflowX: "auto"
});
var panelStyle = (theme, options) => ({
  display: options?.direction ? "flex" : void 0,
  flexDirection: options?.direction,
  flex: options?.flex,
  width: options?.width,
  height: options?.height,
  maxWidth: options?.maxWidth ?? void 0,
  maxHeight: options?.maxHeight ?? void 0,
  overflowX: "auto",
  overflowY: "auto",
  padding: "1px",
  margin: "1px",
  boxSizing: "border-box",
  borderRadius: "6px",
  overflow: options?.maxHeight ? "auto" : void 0,
  backgroundColor: theme.background,
  border: options?.border ? `1px solid ${theme.shadowColor ?? "#ccc"}` : void 0,
  boxShadow: options?.border ? `0 1px 4px rgba(0, 0, 0, 0.08)` : void 0
});

// src/components/view/Container.tsx
import { jsx as jsx15 } from "react/jsx-runtime";
var Container = ({ children }) => {
  const { themeMode } = useThemeMode();
  const activeTheme = themeMode === "light" ? lightTheme : darkTheme;
  return /* @__PURE__ */ jsx15("div", { style: containerStyle(activeTheme), children });
};

// src/components/view/Panel.tsx
import { jsx as jsx16 } from "react/jsx-runtime";
var Panel = ({
  children,
  maxHeight,
  maxWidth,
  width,
  flex,
  height,
  border = true,
  direction,
  style = {}
}) => {
  const { themeMode } = useThemeMode();
  const activeTheme = themeMode === "light" ? lightTheme : darkTheme;
  return /* @__PURE__ */ jsx16(
    "div",
    {
      style: panelStyle(activeTheme, {
        maxWidth,
        maxHeight,
        width,
        height,
        border,
        flex,
        direction
      }),
      children
    }
  );
};

// src/components/view/TreeNode.tsx
import { useState as useState9 } from "react";
import { Fragment as Fragment3, jsx as jsx17, jsxs as jsxs8 } from "react/jsx-runtime";
var TreePanel = ({
  selectedItem,
  selectedNode,
  setSelectedNode,
  handleValueChange
}) => {
  return /* @__PURE__ */ jsxs8(Panel, { flex: "1", direction: "row", border: false, children: [
    /* @__PURE__ */ jsx17(Panel, { width: "400px", children: /* @__PURE__ */ jsx17(
      Tree,
      {
        root: selectedItem,
        showValues: false,
        onSelect: (node) => setSelectedNode(node)
      }
    ) }),
    /* @__PURE__ */ jsx17(Panel, { width: "100%", children: selectedNode?.values && /* @__PURE__ */ jsx17(TreeValues, { values: selectedNode.values, onChange: handleValueChange }) })
  ] });
};
var Tree = ({ root, showValues, onSelect }) => {
  const renderSubtree = (node) => {
    const hasChildren = Array.isArray(node.nodes) && node.nodes.length > 0;
    const hasValues = Array.isArray(node.values) && node.values.length > 0;
    const icon = hasChildren ? "folder-o" : "cog";
    const title = /* @__PURE__ */ jsxs8(
      "div",
      {
        onClick: (e) => {
          e.stopPropagation();
          onSelect?.(node);
        },
        children: [
          /* @__PURE__ */ jsx17("span", { className: `fa fa-${icon}`, style: treeIconStyle() }),
          /* @__PURE__ */ jsx17("span", { style: treeNodeStyle, children: node.name }),
          showValues && hasValues && /* @__PURE__ */ jsxs8(Fragment3, { children: [
            " (",
            node.values.map((val, idx) => /* @__PURE__ */ jsxs8("span", { children: [
              val.key,
              "=",
              val.value,
              idx < node.values.length - 1 ? ", " : ""
            ] }, idx)),
            ")"
          ] })
        ]
      }
    );
    return /* @__PURE__ */ jsx17(TreeItem, { title, children: hasChildren && node.nodes.map((child) => /* @__PURE__ */ jsx17("div", { children: renderSubtree(child) }, child.name)) }, node.name);
  };
  if (!root) {
    return /* @__PURE__ */ jsx17("div", { children: "Nenhum dado dispon\xEDvel." });
  }
  return /* @__PURE__ */ jsx17("div", { children: renderSubtree(root) });
};
var TreeItem = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState9(false);
  return /* @__PURE__ */ jsxs8("div", { children: [
    /* @__PURE__ */ jsxs8("div", { style: { display: "flex", cursor: "pointer" }, onClick: () => setIsOpen(!isOpen), children: [
      /* @__PURE__ */ jsx17("div", { style: { width: 10 }, children: children && /* @__PURE__ */ jsx17("span", { style: { marginLeft: "auto", fontSize: "1.1em" }, children: isOpen ? "\u25BE" : "\u25B8" }) }),
      /* @__PURE__ */ jsx17("div", { children: title })
    ] }),
    isOpen && /* @__PURE__ */ jsx17("div", { style: { marginLeft: "15px" }, children })
  ] });
};
var TreeValues = ({
  values,
  onChange
}) => {
  if (!values || values.length === 0) {
    return /* @__PURE__ */ jsx17("div", { children: "Nenhum valor dispon\xEDvel." });
  }
  return /* @__PURE__ */ jsx17(
    "table",
    {
      style: {
        borderCollapse: "collapse",
        margin: 1,
        width: "98%",
        maxHeight: "170px"
      },
      children: /* @__PURE__ */ jsx17("tbody", { children: values.map((val, index) => /* @__PURE__ */ jsxs8("tr", { children: [
        /* @__PURE__ */ jsx17("td", { style: cellStyle, children: val.key }),
        /* @__PURE__ */ jsx17("td", { style: cellStyle, children: val.key.toLowerCase() === "type" ? /* @__PURE__ */ jsx17("span", { children: val.value }) : /* @__PURE__ */ jsx17(
          "input",
          {
            type: "text",
            value: val.value,
            onChange: (e) => onChange(index, e.target.value),
            style: cellInputStyle
          }
        ) })
      ] }, index)) })
    }
  );
};
var cellStyle = {
  border: "1px solid #ccc",
  padding: "1px 1px",
  textAlign: "left",
  fontSize: "0.75rem"
};
var cellInputStyle = {
  width: "100%",
  padding: "1px",
  fontSize: "0.75rem"
};
var treeNodeStyle = {
  fontSize: "0.55rem",
  fontWeight: "bold"
};
var treeIconStyle = () => ({
  paddingLeft: "5px",
  paddingRight: "5px",
  cursor: "pointer"
});

// src/components/view/NodeTreeView.tsx
import { jsx as jsx18 } from "react/jsx-runtime";
var NodeTreeView = ({ items }) => {
  const [selectedItem, setSelectedItem] = useState10(null);
  const [selectedNode, setSelectedNode] = useState10(null);
  useEffect5(() => {
    if (items) {
      setSelectedNode(items);
      setSelectedItem(items);
    }
  }, [items]);
  const handleValueChange = (index, newValue) => {
    if (!selectedNode || !selectedNode.values) return;
    const updatedValues = selectedNode.values.map(
      (val, i) => i === index ? { ...val, value: newValue } : val
    );
    const updatedNode = {
      ...selectedNode,
      values: updatedValues
    };
    setSelectedNode(updatedNode);
  };
  return /* @__PURE__ */ jsx18(Container, { children: /* @__PURE__ */ jsx18(Panel, { flex: "1", direction: "row", border: false, children: /* @__PURE__ */ jsx18(Panel, { flex: "1", direction: "column", border: false, children: /* @__PURE__ */ jsx18(
    TreePanel,
    {
      selectedItem,
      selectedNode,
      setSelectedNode,
      handleValueChange
    }
  ) }) }) });
};

// src/components/ViewContent.tsx
import { Fragment as Fragment4, jsx as jsx19 } from "react/jsx-runtime";
var ViewContent = ({ name }) => {
  const { t } = useTranslation();
  const appName = useAppName();
  const viewData = useViewNode();
  const rootNode = viewData?.root;
  const formNode = findForm(rootNode, appName, name);
  return /* @__PURE__ */ jsx19(Fragment4, { children: rootNode && /* @__PURE__ */ jsx19(NodeTreeView, { items: rootNode }) });
};

// src/components/Content.tsx
import { jsx as jsx20, jsxs as jsxs9 } from "react/jsx-runtime";
var Content = ({ type, name }) => {
  const { themeMode } = useThemeMode();
  const activeTheme = themeMode === "light" ? lightTheme : darkTheme;
  const appName = useAppName();
  console.log(`[Content] app: ${appName} | type: ${type} | name: ${name}`);
  return /* @__PURE__ */ jsxs9("main", { style: contentMain(), children: [
    /* @__PURE__ */ jsxs9("span", { style: contentTitle(), children: [
      type,
      "|",
      name
    ] }),
    /* @__PURE__ */ jsxs9("div", { style: contentContainer(activeTheme), children: [
      type === "html" && /* @__PURE__ */ jsx20(HtmlContent, { name }),
      type === "view" && /* @__PURE__ */ jsx20(ViewContent, { name })
    ] })
  ] });
};

// src/page/ContentPage.tsx
import { jsx as jsx21, jsxs as jsxs10 } from "react/jsx-runtime";
var ContentPage = ({ load, type: propType, name: propName }) => {
  const { type: urlType, name: urlName } = useParams();
  const type = propType || urlType || "html";
  const name = propName || urlName || "home";
  const appName = useAppName();
  const viewData = useViewNode();
  const rootNode = viewData?.root;
  const menuNode = findMenu2(rootNode, appName, "MAIN");
  return /* @__PURE__ */ jsxs10("div", { style: pageStyle(), children: [
    load.includes("header") && /* @__PURE__ */ jsx21(Header, { load, name: appName }),
    /* @__PURE__ */ jsxs10("div", { style: pageContainerStyle(), children: [
      load.includes("sidebar") && menuNode && /* @__PURE__ */ jsx21(Sidebar, { items: menuNode }),
      /* @__PURE__ */ jsx21(Content, { application: appName, type, name })
    ] }),
    load.includes("footer") && /* @__PURE__ */ jsx21(Footer, { application: appName })
  ] });
};

// src/page/LoginPage.tsx
import { useState as useState11, useRef as useRef3, useEffect as useEffect6 } from "react";
import { useNavigate } from "react-router-dom";
import { jsx as jsx22, jsxs as jsxs11 } from "react/jsx-runtime";
var LoginPage = () => {
  const { t } = useTranslation();
  const { themeMode } = useThemeMode();
  const activeTheme = themeMode === "light" ? lightTheme : darkTheme;
  const { setAlert } = useAlert();
  const appName = useAppName();
  const navigate = useNavigate();
  const [email, setEmail] = useState11("");
  const [password, setPassword] = useState11("");
  const [focusedField, setFocusedField] = useState11(null);
  const [isBtnHovered, setIsBtnHovered] = useState11(false);
  const emailRef = useRef3(null);
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  useEffect6(() => {
    emailRef.current?.focus();
  }, []);
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const ok = await login("newsoftware", email, password);
      if (ok) {
        setAlert(t("viewLoginMsgOk"), "success");
        await sleep(2e3);
        navigate("/content/dashboard");
      }
    } catch (err) {
      setAlert(t("viewLoginMsgErr2"), "error");
    }
  };
  const { login } = useAuth();
  const getInputStyle = (fieldName) => ({
    ...formInput,
    ...focusedField === fieldName ? formInputFocus : {}
  });
  return /* @__PURE__ */ jsx22("div", { style: pageMain(), children: /* @__PURE__ */ jsx22("div", { style: pageContainer(activeTheme), children: /* @__PURE__ */ jsxs11("form", { onSubmit: handleSubmit, style: { width: "100%", maxWidth: "350px" }, children: [
    /* @__PURE__ */ jsx22(
      "img",
      {
        src: `/images/${appName}-logo.png`,
        style: { display: "block", margin: "0 auto 20px", height: "200px" },
        alt: "Logo"
      }
    ),
    /* @__PURE__ */ jsx22("h2", { style: formTitle(activeTheme), children: t("viewLoginTitle") }),
    /* @__PURE__ */ jsx22("label", { style: formLabel(activeTheme), children: t("viewLoginLabelId") }),
    /* @__PURE__ */ jsx22(
      "input",
      {
        ref: emailRef,
        style: formInput(activeTheme, focusedField === "email"),
        onFocus: () => setFocusedField("email"),
        onBlur: () => setFocusedField(null),
        value: email,
        onChange: (e) => setEmail(e.target.value),
        placeholder: "Email"
      }
    ),
    /* @__PURE__ */ jsx22("label", { style: formLabel(activeTheme), children: t("viewLoginLabelPasswod") }),
    /* @__PURE__ */ jsx22(
      "input",
      {
        type: "password",
        style: formInput(activeTheme, focusedField === "password"),
        onFocus: () => setFocusedField("password"),
        onBlur: () => setFocusedField(null),
        value: password,
        onChange: (e) => setPassword(e.target.value),
        placeholder: "Password"
      }
    ),
    /* @__PURE__ */ jsx22(
      "button",
      {
        type: "submit",
        onMouseEnter: () => setIsBtnHovered(true),
        onMouseLeave: () => setIsBtnHovered(false),
        style: formButton(activeTheme),
        children: t("viewLoginTitle")
      }
    )
  ] }) }) });
};

// src/page/UnauthorizedPage.tsx
import { jsx as jsx23, jsxs as jsxs12 } from "react/jsx-runtime";
var UnauthorizedPage = () => {
  const { t } = useTranslation();
  const { themeMode } = useThemeMode();
  const activeTheme = themeMode === "light" ? lightTheme : darkTheme;
  return /* @__PURE__ */ jsx23("div", { style: pageMain(), children: /* @__PURE__ */ jsx23("div", { style: pageContainer(activeTheme), children: /* @__PURE__ */ jsxs12("div", { style: formFrame(), children: [
    /* @__PURE__ */ jsx23("h2", { style: pageErrorTitle(activeTheme), children: t("err403Title") || "403 - Unauthorized" }),
    /* @__PURE__ */ jsx23("p", { style: pageErrorTitle(activeTheme), children: t("err403Message") || "You do not have permission to access this page." }),
    /* @__PURE__ */ jsx23("button", { style: formButton(activeTheme), onClick: () => window.location.href = "/", children: t("btnBackToHome") || "CLOSE" })
  ] }) }) });
};
export {
  ContentPage,
  LoginPage,
  NewSoftwareShell,
  PrivateRoute,
  UnauthorizedPage
};
